import { commitAzureBlockList, putAzureBlock } from '@/api/azure-blob';
import type { UploaderFile } from '@/types/video-uploader';
import type { CancelToken } from 'axios';

interface UseAzureChunkUploadOptions {
    getSasUrl: (fileId: string) => string;
    onUploadedBytes?: (bytes: number) => void;
    cancelToken?: CancelToken;
    isCancelled?: () => boolean;
    chunkSize?: number;
    concurrency?: number;
    retries?: number;
    retryDelayMs?: number;
}

interface UploadFileChunksResult {
    blockIDs: string[];
}

const DEFAULT_CHUNK_SIZE = 20 * 1048576;
const DEFAULT_CONCURRENCY = 5;
const DEFAULT_RETRIES = 3;
const DEFAULT_RETRY_DELAY_MS = 500;

export function useAzureChunkUpload({
    getSasUrl,
    onUploadedBytes,
    cancelToken,
    isCancelled,
    chunkSize = DEFAULT_CHUNK_SIZE,
    concurrency = DEFAULT_CONCURRENCY,
    retries = DEFAULT_RETRIES,
    retryDelayMs = DEFAULT_RETRY_DELAY_MS,
}: UseAzureChunkUploadOptions) {
    async function uploadFileChunksParallel(file: UploaderFile): Promise<UploadFileChunksResult> {
        const sourceFile = file.data;

        if (!sourceFile) {
            throw new Error('File data is unavailable');
        }

        const chunkCount = Math.ceil(sourceFile.size / chunkSize);
        const blockIDs = Array.from({ length: chunkCount }, (_, index) => generateBlockID(index));
        let nextChunkIndex = 0;

        const worker = async () => {
            while (nextChunkIndex < chunkCount) {
                throwIfCancelled();

                const chunkIndex = nextChunkIndex++;
                const start = chunkIndex * chunkSize;
                const end = Math.min(start + chunkSize, sourceFile.size);
                const blob = sourceFile.slice(start, end, sourceFile.type);
                const url = generateUrlForBlock(file.id, blockIDs[chunkIndex]);

                await uploadBlockWithRetry(blob, url);
                onUploadedBytes?.(blob.size);
            }
        };

        const workerCount = Math.min(concurrency, chunkCount);
        const uploadWorkers = Array.from({ length: workerCount }, () => worker());
        await Promise.all(uploadWorkers);

        return { blockIDs };
    }

    async function uploadBlockWithRetry(data: Blob, url: string): Promise<number> {
        for (let attempt = 0; attempt <= retries; attempt++) {
            throwIfCancelled();

            try {
                await putAzureBlock(url, data, cancelToken);
                return attempt;
            } catch (error) {
                throwIfCancelled();
                if (attempt === retries) {
                    throw error;
                }

                await delay(retryDelayMs * 2 ** attempt);
            }
        }

        return retries;
    }

    async function commitBlockList(sasUrl: string, blockIDs: string[], contentType = 'video/mp4'): Promise<void> {
        throwIfCancelled();
        await commitAzureBlockList(sasUrl, blockIDs, contentType, cancelToken);
    }

    function generateBlockID(index: number): string {
        return btoa(`block-${String(index).padStart(6, '0')}`);
    }

    function generateUrlForBlock(fileId: string, blockID: string): string {
        return `${getSasUrl(fileId)}&comp=block&blockid=${blockID}`;
    }

    function delay(milliseconds: number): Promise<void> {
        return new Promise(resolve => {
            setTimeout(resolve, milliseconds);
        });
    }

    function throwIfCancelled(): void {
        if (isCancelled?.()) {
            throw new Error('Video upload cancelled');
        }
    }

    return {
        uploadFileChunksParallel,
        commitBlockList,
    };
}
